/** The command queue (08-clients.md): what the till did that the server has not confirmed. */
import { db, type LocalOrder, type QueuedCommand } from './db.js';

export type NewQueued = Omit<
  QueuedCommand,
  'seq' | 'status' | 'attempts' | 'createdAt' | 'notBefore' | 'code' | 'detail'
>;

export async function enqueue(c: NewQueued): Promise<QueuedCommand> {
  const row: QueuedCommand = {
    ...c,
    status: 'queued',
    attempts: 0,
    createdAt: Date.now(),
    notBefore: 0,
    code: null,
    detail: null,
  };
  const seq = await db().queue.add(row);
  return { ...row, seq };
}

/** Commands still to be sent, in the order they were made. */
export function waiting(outletId?: string): Promise<QueuedCommand[]> {
  const q = db().queue.where('status').equals('queued');
  return (outletId ? q.filter((c) => c.outletId === outletId) : q).sortBy('seq');
}

/** Commands the server refused that a person must look at. */
export async function problems(outletId?: string): Promise<QueuedCommand[]> {
  const rows = await db()
    .queue.where('status')
    .anyOf('conflict', 'rejected')
    .filter((c) => (outletId ? c.outletId === outletId : true))
    .toArray();
  return rows.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
}

/** Is anything for this order still waiting (so a new command on it must queue behind it)? */
export async function hasQueuedFor(orderId: string | null): Promise<boolean> {
  if (orderId === null) return false;
  const n = await db()
    .queue.where('orderId')
    .equals(orderId)
    .filter((c) => c.status === 'queued')
    .count();
  return n > 0;
}

export async function pendingCount(outletId?: string): Promise<number> {
  return (await waiting(outletId)).length;
}

/** Remove a confirmed command, and settle the order's pending count. */
export async function confirm(seq: number): Promise<void> {
  const row = await db().queue.get(seq);
  await db().queue.delete(seq);
  if (row?.orderId) await recount(row.orderId);
}

export async function recount(orderId: string): Promise<void> {
  const pending = await db()
    .queue.where('orderId')
    .equals(orderId)
    .filter((c) => c.status === 'queued')
    .count();
  await db().orders.where('id').equals(orderId).modify({ pending });
}

export async function localOrder(orderId: string): Promise<LocalOrder | undefined> {
  return db().orders.get(orderId);
}

export async function liveLocalOrders(outletId: string): Promise<LocalOrder[]> {
  return db().orders.where('outletId').equals(outletId).toArray();
}
